import React from 'react'
import { Link } from 'react-router-dom'
import SEOHead from '../components/SEOHead'
import './CommanderTrailersPage.css'

interface TrailerModel {
  model: string;
  capacity: number;
  boatLength: string;
  overallLength: string;
  gvwr: number;
  axles: number;
  brakes: string;
  couplerSize: string;
  tireSize: string;
  tongueJack: string;
}

const CommanderTrailersPage: React.FC = () => {
  const singleAxleModels: TrailerModel[] = [
    {
      model: "COM-2900",
      capacity: 2900,
      boatLength: "18'-20'",
      overallLength: "22'1\"",
      gvwr: 3600,
      axles: 1,
      brakes: "No Brakes",
      couplerSize: "2\"",
      tireSize: "ST205/75R 14 LRC 5H",
      tongueJack: "Single Wheel"
    },
    {
      model: "COM-3100",
      capacity: 3100,
      boatLength: "18'-20'",
      overallLength: "22'10\"",
      gvwr: 3800,
      axles: 1,
      brakes: "Disc-1",
      couplerSize: "2\"",
      tireSize: "ST215/75R 14 LRC 5H",
      tongueJack: "Single Wheel"
    },
    {
      model: "COM-3400",
      capacity: 3400,
      boatLength: "20'-22'",
      overallLength: "24'",
      gvwr: 4175,
      axles: 1,
      brakes: "Disc-1",
      couplerSize: "2\"",
      tireSize: "ST225/75R 15 LRD 5H",
      tongueJack: "Single Wheel"
    }
  ];

  const tandemAxleModels: TrailerModel[] = [
    {
      model: "COM-3700",
      capacity: 3700,
      boatLength: "18'-20'",
      overallLength: "22'10\"",
      gvwr: 4650,
      axles: 2,
      brakes: "Disc-2",
      couplerSize: "2\"",
      tireSize: "ST205/75R 14 LRC 5H",
      tongueJack: "Single Wheel"
    },
    {
      model: "COM-5300",
      capacity: 5300,
      boatLength: "20'-22'",
      overallLength: "24'",
      gvwr: 6250,
      axles: 2,
      brakes: "Disc-2",
      couplerSize: "2\"",
      tireSize: "ST205/75R 14 LRC 5H",
      tongueJack: "Single Wheel"
    },
    {
      model: "COM-6000",
      capacity: 6000,
      boatLength: "22'-24'",
      overallLength: "24'11\"",
      gvwr: 7050,
      axles: 2,
      brakes: "Disc-2",
      couplerSize: "2\"",
      tireSize: "ST205/75R 14 LRC 5H",
      tongueJack: "2000lb Foot Jack"
    },
    {
      model: "COM-6500",
      capacity: 6500,
      boatLength: "24'-26'",
      overallLength: "28'",
      gvwr: 7800,
      axles: 2,
      brakes: "Disc-2",
      couplerSize: "2\"",
      tireSize: "ST225/75R 15 LRD 5H",
      tongueJack: "2000lb Foot Jack"
    },
    {
      model: "COM-7300",
      capacity: 7300,
      boatLength: "24'-26'",
      overallLength: "28'",
      gvwr: 8600,
      axles: 2,
      brakes: "Disc-2",
      couplerSize: "2 5/16\"",
      tireSize: "ST225/75R 15 LRD 5H",
      tongueJack: "2000lb Foot Jack"
    },
    {
      model: "COM-8100",
      capacity: 8100,
      boatLength: "25'-27'",
      overallLength: "30'1\"",
      gvwr: 9440,
      axles: 2,
      brakes: "Disc-2",
      couplerSize: "2 5/16\"",
      tireSize: "ST225/75R 15 LRD 6H",
      tongueJack: "2000lb Foot Jack"
    },
    {
      model: "COM-8800",
      capacity: 8800,
      boatLength: "27'-29'",
      overallLength: "30'1\"",
      gvwr: 10160,
      axles: 2,
      brakes: "Disc-2",
      couplerSize: "2 5/16\"",
      tireSize: "ST225/75R 15 LRD 6H",
      tongueJack: "2000lb Foot Jack"
    }
  ];

  const tripleAxleModels: TrailerModel[] = [
    {
      model: "COM-10800",
      capacity: 10800,
      boatLength: "28'-30'",
      overallLength: "31'1\"",
      gvwr: 12730,
      axles: 3,
      brakes: "Disc-3",
      couplerSize: "2 5/16\"",
      tireSize: "ST225/75R 15 LRD 6H",
      tongueJack: "2000lb Foot Jack"
    },
    {
      model: "COM-12600",
      capacity: 12600,
      boatLength: "29'-33'",
      overallLength: "37'1\"",
      gvwr: 14610,
      axles: 3,
      brakes: "Disc-3",
      couplerSize: "2 5/16\"",
      tireSize: "ST225/75R 15 LRD 6H",
      tongueJack: "2000lb Foot Jack"
    }
  ];

  const premiumFeatures = [
    "Galvanized hardware for superior corrosion resistance",
    "LED lighting systems for enhanced visibility",
    "Premium aluminum sport rims",
    "Adjustable target bunks for perfect boat fit",
    "Load guides for easy launch and retrieval",
    "Vortex hubs with 6-year/100,000-mile warranty",
    "Balanced radial tires for smooth towing",
    "Durable poly bunk covers",
    "Aluminum diamond plate fenders",
    "Aluminum crossmembers for lightweight strength",
    "Galvanized torsion axles for superior ride quality",
    "DOT-approved composite brake line",
    "Plug and play lighting connections",
    "Disc brakes with GALVX coating (1000-hour salt spray test)"
  ];

  return (
    <div className="commander-page">
      <SEOHead
        title="Commander Boat Trailers - Premium Series | Fast Boat Trailers"
        description="Commander boat trailers from Fast Boat Trailers in Edgewater, MD. Premium heavy-duty trailers with advanced features for larger boats. Single, tandem, and triple axle configurations."
        keywords="Commander trailers, premium boat trailers Maryland, heavy duty trailers, Commander series trailers, large boat trailers"
        ogTitle="Commander Boat Trailers - Premium Series"
        ogDescription="Commander boat trailers - premium heavy-duty series with advanced features for larger boats. Single, tandem, and triple axle options."
        ogImage="/images/trailers/commander.jpg"
        canonical="https://fastboattrailers.com/trailers/commander"
      />
      <div className="container">
        {/* Hero Section */}
        <div className="commander-hero">
          <div className="hero-main">
            <div className="hero-content">
              <h1>Commander Series</h1>
              <h2>Built for the Long Haul</h2>
              <p>
                The Venture Trailers Commander Series represents the pinnacle of marine transport engineering. 
                Built for serious boaters who demand premium features and uncompromising quality, each Commander 
                trailer comes equipped with sleek, high-end features that come standard with every model.
              </p>
              
              <h3>Benefits of Commander Series</h3>
              <ul className="benefits-list">
                <li>Premium aluminum construction for lightweight strength</li>
                <li>Superior corrosion resistance with galvanized hardware</li>
                <li>Enhanced safety features with disc brakes and LED lighting</li>
                <li>Long-lasting performance with 6-year/100,000-mile warranty</li>
              </ul>
            </div>
            <div className="hero-features">
              <h3>Premium Features Standard</h3>
              <div className="features-compact">
                {premiumFeatures.map((feature, index) => (
                  <div key={index} className="feature-item-compact">
                    <div className="feature-icon-compact">✓</div>
                    <span>{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Models Section */}
        <div className="models-section">
          <h2>Commander Series Models</h2>
          
          {/* Single Axle Models */}
          <div className="model-category">
            <div className="category-image-full">
              <img src="/images/trailers/commander-single-axle.jpg" alt="Commander Single Axle Trailer" />
            </div>
            <div className="category-header">
              <h3>Single Axle Models</h3>
              <p>Perfect for smaller boats requiring reliable, lightweight transport solutions.</p>
            </div>
            <div className="models-table-container">
              <table className="models-table">
                <thead>
                  <tr>
                    <th>Model</th>
                    <th>Capacity</th>
                    <th>Boat Length</th>
                    <th>Overall Length</th>
                    <th>GVWR</th>
                    <th>Brakes</th>
                    <th>Coupler</th>
                    <th>Tires</th>
                  </tr>
                </thead>
                <tbody>
                  {singleAxleModels.map((model, index) => (
                    <tr key={index}>
                      <td className="model-name">{model.model}</td>
                      <td>{model.capacity.toLocaleString()} lbs</td>
                      <td>{model.boatLength}</td>
                      <td>{model.overallLength}</td>
                      <td>{model.gvwr.toLocaleString()} lbs</td>
                      <td>{model.brakes}</td>
                      <td>{model.couplerSize}</td>
                      <td>{model.tireSize}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Tandem Axle Models */}
          <div className="model-category">
            <div className="category-image-full">
              <img src="/images/trailers/commander-tandem-axle.jpg" alt="Commander Tandem Axle Trailer" />
            </div>
            <div className="category-header">
              <h3>Tandem Axle Models</h3>
              <p>Ideal for mid-size boats requiring enhanced stability and load distribution.</p>
            </div>
            <div className="models-table-container">
              <table className="models-table">
                <thead>
                  <tr>
                    <th>Model</th>
                    <th>Capacity</th>
                    <th>Boat Length</th>
                    <th>Overall Length</th>
                    <th>GVWR</th>
                    <th>Brakes</th>
                    <th>Coupler</th>
                    <th>Tires</th>
                  </tr>
                </thead>
                <tbody>
                  {tandemAxleModels.map((model, index) => (
                    <tr key={index}>
                      <td className="model-name">{model.model}</td>
                      <td>{model.capacity.toLocaleString()} lbs</td>
                      <td>{model.boatLength}</td>
                      <td>{model.overallLength}</td>
                      <td>{model.gvwr.toLocaleString()} lbs</td>
                      <td>{model.brakes}</td>
                      <td>{model.couplerSize}</td>
                      <td>{model.tireSize}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Triple Axle Models */}
          <div className="model-category">
            <div className="category-image-full">
              <img src="/images/trailers/commander-triple-axle.jpg" alt="Commander Triple Axle Trailer" />
            </div>
            <div className="category-header">
              <h3>Triple Axle Models</h3>
              <p>Heavy-duty solutions for large boats demanding maximum capacity and stability.</p>
            </div>
            <div className="models-table-container">
              <table className="models-table">
                <thead>
                  <tr>
                    <th>Model</th>
                    <th>Capacity</th>
                    <th>Boat Length</th>
                    <th>Overall Length</th>
                    <th>GVWR</th>
                    <th>Brakes</th>
                    <th>Coupler</th>
                    <th>Tires</th>
                  </tr>
                </thead>
                <tbody>
                  {tripleAxleModels.map((model, index) => (
                    <tr key={index}>
                      <td className="model-name">{model.model}</td>
                      <td>{model.capacity.toLocaleString()} lbs</td>
                      <td>{model.boatLength}</td>
                      <td>{model.overallLength}</td>
                      <td>{model.gvwr.toLocaleString()} lbs</td>
                      <td>{model.brakes}</td>
                      <td>{model.couplerSize}</td>
                      <td>{model.tireSize}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="commander-cta">
          <h2>Ready for a Commander Series Trailer?</h2>
          <p>
            Experience the difference that premium engineering makes. Our Commander Series trailers 
            are built for boaters who demand the best in quality, durability, and performance.
          </p>
          <div className="cta-buttons">
            <Link to="/contact" className="btn btn-primary">Get a quote</Link>
          </div>
        </div>
      </div>
    </div>
  )
}

export default CommanderTrailersPage